'use client';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';
import { getHighlightPositions } from '@/lib/utils';
import useEmblaCarousel from 'embla-carousel-react';

const stripInlineStyles = (html: string): string => {
  if (!html) return '';
  return html.replace(/\s*style="[^"]*"/gi, '')
    .replace(/\s*style='[^']*'/gi, '');
};

interface Event {
  id: string;
  duration: string;
  schedule?: string;
  description: string;
  image: string;
  slug?: string;
}

interface EventsCarouselProps {
  title?: string | React.ReactNode;
  subtitle?: string;
  buttonText?: string;
  events: Event[];
  className?: string;
  highlight_title?: string | string[];
}

const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.25 0V1.5H9.75V0H11.25V1.5H14.25C14.6642 1.5 15 1.83579 15 2.25V14.25C15 14.6642 14.6642 15 14.25 15H0.75C0.33579 15 0 14.6642 0 14.25V2.25C0 1.83579 0.33579 1.5 0.75 1.5H3.75V0H5.25ZM13.5 7.5H1.5V13.5H13.5V7.5ZM4.5 9V10.5H3V9H4.5ZM8.25 9V10.5H6.75V9H8.25ZM12 9V10.5H10.5V9H12ZM3.75 3H1.5V6H13.5V3H11.25V4.5H9.75V3H5.25V4.5H3.75V3Z" fill="#F7941D" />
  </svg>
);

/*   const ArrowIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ); */

interface EventCardProps {
  event: Event;
  isHovered?: boolean;
  onHoverChange?: (isHovered: boolean) => void;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onHoverChange,
}) => {
  const [imageError, setImageError] = useState(false);

  const cleanDescription = useMemo(
    () => stripInlineStyles(event.description),
    [event.description]
  );

  // Get image URL - handle both string and object formats
  const imageUrl = useMemo(() => {
    if (!event.image) return '/event.png';

    // If it's a string, use it directly
    if (typeof event.image === 'string') {
      return event.image || '/event.png';
    }

    // If it's an object with url property
    if (typeof event.image === 'object' && 'url' in event.image) {
      return (event.image as any).url || '/event.png';
    }

    return '/event.png';
  }, [event.image]);

  return (
    <Link href={event.slug ? `/noticias-eventos/eventos/${event.slug}` : '#'} className="block h-full">
      <article
        className="w-full h-full bg-white rounded-sm shadow-[0px_8.542858123779297px_20.26080894470215px_0px_rgba(139,141,142,0.15)] overflow-hidden flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group cursor-pointer"
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
      >
        {/* Image Section */}
        <div className="w-full h-48 sm:h-52 md:h-40 relative overflow-hidden bg-gray-200">
          <Image
            src={!imageError ? imageUrl : '/event.png'}
            alt={event.description || 'Evento'}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-110"
            sizes="(max-width: 768px) 85vw, 320px"
            onError={() => setImageError(true)}
            priority={false}
          />
        </div>

        {/* Content Section */}
        <div className="px-4 py-4 flex flex-col gap-2.5 grow">
          {/* Date */}
          <div className="flex items-center gap-2">
            <CalendarIcon />
            <span className="text-orangefpef text-xs font-medium font-poppins uppercase">
              {event.duration}
            </span>
          </div>

          {/* Description */}
          <div className="text-[#252122] text-sm font-semibold font-nunito leading-snug line-clamp-3 grow">
            {cleanDescription}
          </div>

          {/* More Details Link */}
          <div className="text-[#F15A29] text-sm font-normal font-poppins hover:underline transition-all duration-200 text-left mt-auto">
            + detalhes
          </div>
        </div>
      </article>
    </Link>
  );
};

export const EventsCarousel: React.FC<EventsCarouselProps> = ({
  title = 'Próximos Eventos',
  /*  subtitle = 'Eventos',*/
  buttonText = 'Eventos',
  events,
  className = '',
  highlight_title = 'INICIATIVAS E EVENTOS',
}) => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(events.length / itemsPerPage);

  // Estado para drag no desktop
  const [isDragging, setIsDragging] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Embla Carousel para mobile
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    skipSnaps: false,
    dragFree: false
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  // Tratamento quando não há eventos
  if (!events || events.length === 0) {
    return (
      <section className={`w-full bg-stone-100 py-16 px-4 md:px-8 ${className}`}>
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg font-poppins">
              Nenhum evento disponível no momento
            </p>
          </div>
        </div>
      </section>
    );
  }

  const goToSlide = (index: number) => {
    setCurrentIndex(Math.max(0, Math.min(index, totalPages - 1)));
  };

  // Handlers para drag no desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setTouchStart(e.clientX);
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setTouchEnd(e.clientX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);

    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentIndex < totalPages - 1) {
      goToSlide(currentIndex + 1);
    }

    if (isRightSwipe && currentIndex > 0) {
      goToSlide(currentIndex - 1);
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setTouchStart(0);
      setTouchEnd(0);
    }
  };

  const visibleEvents = events.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <section className={`w-full bg-stone-100 py-12 sm:py-14 md:py-16 lg:py-24 px-4 md:px-8 ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
        {/* Desktop Layout - Grid com Paginação */}
        <div className="hidden w-full xl:grid xl:grid-cols-[3fr_1fr] gap-8 lg:gap-12 items-start justify-center">
          {/* Right Column - Cards Grid */}
          <div
            className="relative w-full cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {/* Grid de Cards - Desktop */}
            <div className="grid grid-cols-3 gap-6 lg:gap-8 transition-opacity duration-300">
              {visibleEvents.map((event) => (
                <div key={event.id} className="w-full max-w-[320px] mx-auto lg:mx-0">
                  <EventCard
                    event={event}
                    isHovered={hoveredCard === event.id}
                    onHoverChange={(isHovered) =>
                      setHoveredCard(isHovered ? event.id : null)
                    }
                  />
                </div>
              ))}
            </div>

            {/* Pagination Dots - Desktop */}
            {totalPages > 1 && (
              <div className="flex gap-3 mt-8 justify-start" role="tablist" aria-label="Páginas de eventos">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    role="tab"
                    aria-selected={index === currentIndex}
                    aria-label={`Página ${index + 1} de ${totalPages}`}
                    className={`rounded-full transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 ${index === currentIndex
                      ? 'w-15 h-2 bg-amber-600'
                      : 'w-8 h-2 bg-gray-400 hover:bg-gray-500'
                      }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Left Column - Title and Button */}
          <div className="space-y-8 pt-4 flex flex-col items-end">
            {/* Title */}
            <div className="max-w-[338px] text-right">
              {typeof title === 'string' ? (
                (() => {
                  const sentence = title || "";
                  const keywords = highlight_title || "";
                  const positions = getHighlightPositions(sentence, keywords);
                  return (
                    <HighlightedHeading
                      text={sentence}
                      positions={positions}
                      className='text-right! '
                    />
                  );
                })()
              ) : (
                <h2 className="text-gray-800 text-3xl font-bold font-rubik leading-tight">
                  {title}
                </h2>
              )}
            </div>

            {/* Button */}
            <button
              type="button"
              onClick={() => router.push('/noticias-eventos')}
              className="cursor-pointer rounded-md border-2 border-[#F15A29] px-6 py-3 flex items-center gap-2 hover:bg-[#F15A29] hover:text-white transition-colors group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F15A29]"
              aria-label={`Ver ${buttonText}`}
            >
              <span className="text-[#F15A29] group-hover:text-white text-base font-medium font-nunito">
                {buttonText}
              </span>
            </button>
          </div>
        </div>

        {/* Mobile & Tablet Layout - Carousel com Embla */}
        <div className="xl:hidden space-y-6">
          {/* Header - Title & Button */}
          <div className="space-y-4">
            {/* Title */}
            <div className="text-center">
              {typeof title === 'string' ? (
                (() => {
                  const sentence = title || "";
                  const keywords = highlight_title || "";
                  const positions = getHighlightPositions(sentence, keywords);
                  return (
                    <HighlightedHeading
                      text={sentence}
                      positions={positions}
                      className='text-center'
                    />
                  );
                })()
              ) : (
                <h2 className="text-gray-800 text-xl font-medium font-barlow uppercase leading-7">
                  {title}
                </h2>
              )}
            </div>

            {/* Button */}
            <button
              type="button"
              onClick={() => router.push('/noticias-eventos')}
              className="cursor-pointer rounded-sm border-2 border-[#F15A29] px-6 py-2.5 flex items-center justify-center gap-2 hover:bg-[#F15A29] hover:text-white transition-colors group w-full sm:w-auto min-w-[200px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F15A29] mx-auto"
              aria-label={`Ver ${buttonText}`}
            >
              <span className="text-[#F15A29] group-hover:text-white text-lg font-medium font-poppins">
                +
              </span>
              <span className="text-[#F15A29] group-hover:text-white text-sm font-medium font-poppins">
                {buttonText}
              </span>
            </button>
          </div>

          {/* Carousel com Embla */}
          <div className="overflow-hidden -mx-4 px-4" ref={emblaRef}>
            <div className="flex gap-4">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="flex-[0_0_85%] sm:flex-[0_0_90%] min-w-0"
                >
                  <EventCard
                    event={event}
                    isHovered={hoveredCard === event.id}
                    onHoverChange={(isHovered) =>
                      setHoveredCard(isHovered ? event.id : null)
                    }
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Indicadores de navegação */}
          <div className="flex justify-center items-center gap-2">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => emblaApi?.scrollTo(index)}
                className={`h-1 rounded-full transition-all duration-300 ${index === selectedIndex
                  ? 'w-8 bg-gradient-to-r from-orange-600 via-orange-500 to-red-500'
                  : 'w-6 bg-gray-300'
                  }`}
                aria-label={`Ir para slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};